import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../components/MainContainer";

import {
  Buttons,
  Description,
  EmptyProfile,
  FollowButton,
  FollowingArrowButton,
  FollowingButtonGroup,
  FollowingMainButton,
  FollowingMenu,
  FollowingMenuItem,
  PlanBadge,
  ProfileHeader,
  ProfileHeaderColumn,
  ProfileHeaderRow,
  ProfilePhoto,
  ShareButton,
  Stats,
  StatsContainer,
  ContentContainer,
  Column,
  Content,
  PaginationWrapper,
  Username,
  EmptyState,
  Button,
  BadgesContainer,
  VipBadge,
  ProfilePhotoContainer,
  EmptyText,
  MedalIcon,
  ProfilePhotoSkeleton,
  TextSkeleton,
  ButtonSkeleton,
  ToastWrapper,
  CopyContainer,
  PinPreviewBackdrop,
  PinPreviewCard,
  PinPreviewImage,
  PinPreviewVideo,
  PinPreviewInfo,
  PinPreviewTitle,
  PinPreviewSubtitle,
  PinActionMenu,
  PinActionItem,
  MedalBadge,
  MobileDescription,
  DesktopTextSkeleton,
  MobileTextSkeleton,
} from "./ProfilePage.styles";

import {
  selectUser,
  selectProfileUser,
  selectToggleFollowPending,
} from "../../store/slices/user/slice";
import {
  getUserCollectionsData,
  toggleFollow,
} from "../../store/slices/user/asyncThunk";
import {
  getExploreCollections,
  togglePinCollection,
} from "../../store/slices/model/asyncThunks";
import {
  selectCategories,
  selectSelectedCollection,
  setSelectedCollection,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import GroupModal from "../../components/GroupModal/GroupModal";
import Pagination from "../../components/Pagination/Pagination";
import { useMediaQuery } from "react-responsive";
import { useSearchParams } from "react-router-dom";
import { LayoutGroup, motion } from "framer-motion";

import FirstPng from "../../assets/images/1.png";
import SecondPng from "../../assets/images/2.png";
import ThirdPng from "../../assets/images/3.png";
import GoldMedal from "../../assets/images/goldMedal.webp";
import SilverMedal from "../../assets/images/silverMedal.webp";

import DefaultProfilePhoto from "../../components/DefaultProfilePhoto/DefaultProfilePhoto";
import { ReactComponent as FollowIcon } from "../../assets/icons/follow.svg";
import { ReactComponent as UnfollowIcon } from "../../assets/icons/unfollow.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share-06.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/editProfile.svg";
import { ReactComponent as EmptyIcon } from "../../assets/icons/empty.svg";
import { ReactComponent as PinIcon } from "../../assets/icons/pin.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";

import {
  BASIC_PLAN_NAME,
  BLEND_MODEL,
  CREATE_IMAGE_MODEL_NAME,
  MEDIA_TYPE,
  PREMIUM_PLAN_NAME,
  PRO_PLAN_NAME,
} from "../../utils/constants";
import { formatModelName, isImageOutput } from "../../utils/utils";
import UserFollowersModal from "../../components/UserFollowersModal/UserFollowersModal";
import loaderAnimation from "../../assets/animations/onLoad.json";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import { toast } from "sonner";
import CreateImageModal from "../../components/CreateImageModal/CreateImageModal";

const getItemId = (item) => item?._id || item?.id;

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isOpenFollowersModal, setIsOpenFollowersModal] = useState(false);
  const [isCreateImageModalOpen, setIsCreateImageModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [openModal] = useState("");
  const [pinnedOverrides, setPinnedOverrides] = useState({});
  const [pinnedAppendOrder, setPinnedAppendOrder] = useState([]);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinActionLoading, setPinActionLoading] = useState(false);
  const [showFollowMenu, setShowFollowMenu] = useState(false);
  const pinTooltipRef = useRef(null);
  const followMenuRef = useRef(null);

  const page = Number(searchParams.get("page")) || 1;
  const { username } = useParams();

  const loggedInUser = useSelector(selectUser);
  const profileUserState = useSelector(selectProfileUser);
  const loggedInUserId = loggedInUser?._id || loggedInUser?.id;
  const loggedInUsername = loggedInUser?.username;

  const isMyProfile = !username || loggedInUsername === username;

  useEffect(() => {
    if (username && loggedInUsername && username === loggedInUsername) {
      navigate("/profile", { replace: true });
    }
  }, [username, loggedInUsername, navigate]);

  const user = isMyProfile ? loggedInUser : profileUserState.data;

  const userNotFound =
    (!isMyProfile && profileUserState.error?.status === 422) ||
    (!!user && Number(user?.activeModels ?? 0) === 0);
  const isProfileLoading = isMyProfile
    ? !loggedInUser
    : !profileUserState.data &&
      (profileUserState.loading || !profileUserState.error);

  const hasActiveSubscription = isMyProfile
    ? user?.subscription?.name &&
      user?.subscription?.endDate &&
      new Date(user?.subscription?.endDate) > new Date()
    : !!user?.subscription?.name;

  const activePlan = hasActiveSubscription ? user?.subscription?.name : null;

  const getMedalSrc = (rank) => {
    if (rank === 1) return FirstPng;
    if (rank === 2) return SecondPng;
    if (rank === 3) return ThirdPng;
    if (rank >= 4 && rank <= 5) return GoldMedal;
    if (rank >= 6 && rank <= 10) return SilverMedal;
    return null;
  };

  const creatorRank = user?.score?.creatorLeaderboardRank;
  const medalSrc = getMedalSrc(creatorRank);

  const { data: categories, error: errorCategories } =
    useSelector(selectCategories);
  const selectedCollectionVideo = useSelector(selectSelectedCollection);
  const loggedIn = useSelector(selectLoggedIn);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const PAGE_SIZE = 20;
  const MAX_PINNED_COLLECTIONS = 4;

  const isItemPinned = useCallback(
    (item) => {
      if (!item) return false;
      const itemId = getItemId(item);
      if (itemId && itemId in pinnedOverrides) return pinnedOverrides[itemId];
      return !!item.isPinned;
    },
    [pinnedOverrides],
  );

  const pinnedCount = useMemo(() => {
    const collections = profileUserState.collections;
    if (!isMyProfile || !collections) return 0;
    return collections.reduce(
      (count, item) => (isItemPinned(item) ? count + 1 : count),
      0,
    );
  }, [profileUserState.collections, isItemPinned, isMyProfile]);

  const canPinCollection = (item) => {
    const itemId = getItemId(item);
    if (!isMyProfile || !loggedInUserId || !itemId) return false;
    if (!item?.user) return true;

    const itemUserId =
      typeof item.user === "string"
        ? item.user
        : item.user?._id || item.user?.id;

    return itemUserId === loggedInUserId;
  };

  const setPinnedOverride = (itemId, value) => {
    setPinnedOverrides((prev) => ({ ...prev, [itemId]: value }));
  };

  const clearPinnedOverride = (itemId) => {
    setPinnedOverrides((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const addPinnedAppendOrder = (itemId) => {
    setPinnedAppendOrder((prev) =>
      prev.includes(itemId) ? prev : [...prev, itemId],
    );
  };

  const removePinnedAppendOrder = (itemId) => {
    setPinnedAppendOrder((prev) => prev.filter((id) => id !== itemId));
  };

  const handlePinConfirm = async () => {
    const pinItem = pinTarget?.item;
    const itemId = getItemId(pinItem);

    if (!itemId || pinActionLoading || !canPinCollection(pinItem)) {
      setPinTarget(null);
      return;
    }

    const wasPinned = isItemPinned(pinItem);
    const wasAppendedPinned = pinnedAppendOrder.includes(itemId);
    const nextPinned = !wasPinned;

    if (!wasPinned && pinnedCount >= MAX_PINNED_COLLECTIONS) {
      setPinTarget(null);
      toast.error(`You can pin up to ${MAX_PINNED_COLLECTIONS} collections.`);
      return;
    }

    setPinnedOverride(itemId, nextPinned);
    if (nextPinned) {
      addPinnedAppendOrder(itemId);
    } else {
      removePinnedAppendOrder(itemId);
    }
    setPinTarget(null);
    setPinActionLoading(true);
    try {
      await dispatch(togglePinCollection({ collectionId: itemId })).unwrap();
    } catch (error) {
      clearPinnedOverride(itemId);
      if (wasAppendedPinned) {
        addPinnedAppendOrder(itemId);
      } else {
        removePinnedAppendOrder(itemId);
      }
      toast.error(
        typeof error === "string"
          ? error
          : error?.message || `Failed to ${wasPinned ? "unpin" : "pin"} post.`,
      );
    } finally {
      setPinActionLoading(false);
    }
  };

  useEffect(() => {
    if (!pinTarget) return;

    const handleOutsideInteraction = (event) => {
      if (
        pinTooltipRef.current &&
        !pinTooltipRef.current.contains(event.target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        setPinTarget(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideInteraction, true);
    document.addEventListener("touchstart", handleOutsideInteraction, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction, true);
      document.removeEventListener(
        "touchstart",
        handleOutsideInteraction,
        true,
      );
    };
  }, [pinTarget]);

  const pinPreviewLayout = useMemo(() => {
    if (!pinTarget?.rect) return null;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const aspectRatio =
      pinTarget.rect.width && pinTarget.rect.height
        ? pinTarget.rect.width / pinTarget.rect.height
        : 0.72;
    const edgePadding = 12;
    const menuHeight = 68;
    const gap = 12;

    let previewWidth = Math.min(
      Math.max(pinTarget.rect.width * 1.08, 180),
      Math.min(360, viewportWidth - edgePadding * 2),
    );
    let previewHeight = previewWidth / Math.max(aspectRatio, 0.45);
    const maxPreviewHeight = Math.max(220, viewportHeight * 0.58);

    if (previewHeight > maxPreviewHeight) {
      previewHeight = maxPreviewHeight;
      previewWidth = previewHeight * Math.max(aspectRatio, 0.45);
    }

    const desiredLeft =
      pinTarget.rect.left - (previewWidth - pinTarget.rect.width) / 2;
    const left = Math.min(
      viewportWidth - previewWidth - edgePadding,
      Math.max(edgePadding, desiredLeft),
    );
    const desiredTop =
      pinTarget.rect.top - (previewHeight - pinTarget.rect.height) / 2 - 6;
    const top = Math.min(
      Math.max(edgePadding, desiredTop),
      Math.max(
        edgePadding,
        viewportHeight - previewHeight - menuHeight - gap - edgePadding,
      ),
    );
    const menuWidth = Math.min(
      178,
      Math.max(136, Math.round(previewWidth * 0.56)),
    );
    const menuLeft = Math.min(
      viewportWidth - menuWidth - edgePadding,
      Math.max(edgePadding, left + previewWidth - menuWidth),
    );
    const menuTop = top + previewHeight + gap;

    return {
      preview: {
        top,
        left,
        width: previewWidth,
        height: previewHeight,
      },
      menu: {
        top: menuTop,
        left: menuLeft,
        width: menuWidth,
      },
    };
  }, [pinTarget]);

  const pinPreviewItem = pinTarget?.item;
  const pinPreviewTitleText = pinPreviewItem?.name
    ? formatModelName(pinPreviewItem.name)
    : "Template";
  const pinPreviewSubtitleText = pinPreviewItem?.username
    ? `@${pinPreviewItem.username}`
    : pinPreviewItem?.output?.type === MEDIA_TYPE.VIDEO
      ? "Video template"
      : "Image template";
  const pinPreviewPoster =
    pinPreviewItem?.input?.image?.resizedImage?.url ||
    pinPreviewItem?.input?.image?.url ||
    null;
  const pinPreviewMediaUrl =
    pinPreviewItem?.output?.type === MEDIA_TYPE.IMAGE
      ? pinPreviewItem?.output?.image?.url || pinPreviewPoster
      : pinPreviewItem?.output?.video?.compressedUrl ||
        pinPreviewItem?.output?.video?.url ||
        pinPreviewPoster;

  const sortedCollections = useMemo(() => {
    const collections = profileUserState.collections;
    if (!collections) return null;
    const pinned = [];
    const appendedPinned = [];
    const others = [];
    const appendedPinnedSet = new Set(pinnedAppendOrder);
    for (const item of collections) {
      if (isItemPinned(item)) {
        if (appendedPinnedSet.has(getItemId(item))) {
          appendedPinned.push(item);
        } else {
          pinned.push(item);
        }
      } else {
        others.push(item);
      }
    }
    if (!pinned.length && !appendedPinned.length) return collections;

    appendedPinned.sort(
      (a, b) =>
        pinnedAppendOrder.indexOf(getItemId(a)) -
        pinnedAppendOrder.indexOf(getItemId(b)),
    );

    return [...pinned, ...appendedPinned, ...others];
  }, [profileUserState.collections, isItemPinned, pinnedAppendOrder]);

  const error = errorCategories || profileUserState.error;
  const data = sortedCollections;
  const loading = profileUserState.loading;
  const total = profileUserState.total ?? 0;
  const perPage = profileUserState.perPage || PAGE_SIZE;

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      default:
        return 5;
    }
  }, [isDesktop, isTable, isMobile]);

  useEffect(() => {
    const usernameToFetch = username || loggedInUsername;
    if (usernameToFetch) {
      dispatch(getUserCollectionsData({ username: usernameToFetch, page }));
    }
  }, [dispatch, username, loggedInUsername, page]);

  useEffect(() => {
    if (!categories?.length) {
      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
        }),
      );
    }
  }, [categories, dispatch]);

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    setTimeout(() => {
      if (page > 1) {
        searchParams.set("page", page);
      } else {
        searchParams.delete("page");
      }

      setSearchParams(searchParams);
    }, 300);
  };

  const togglePendingMap = useSelector(selectToggleFollowPending);
  const isToggleFollowPending = !!togglePendingMap[user?._id];
  const isFollowing = !!user?.isFollowing;

  const handleFollowToggle = () => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }
    if (!user?._id || isToggleFollowPending) return;
    dispatch(toggleFollow(user._id));
  };

  useEffect(() => {
    if (!showFollowMenu) return;

    const handleOutsideClick = (event) => {
      if (
        followMenuRef.current &&
        !followMenuRef.current.contains(event.target)
      ) {
        setShowFollowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showFollowMenu]);

  const handleShare = () => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }
    if (!user?.username) return;
    const shareUrl = `${window.location.origin}/profile/${user.username}?ref=${loggedInUsername}`;
    navigator.clipboard.writeText(shareUrl);

    toast.success(
      <ToastWrapper>
        <div>Profile link copied to clipboard!</div>
        <CopyContainer
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success("Copied to clipboard!");
          }}
        >
          {shareUrl}
        </CopyContainer>
      </ToastWrapper>,
      { icon: null },
    );
  };

  if (userNotFound) {
    return (
      <MainContainer hideTabs={true} fullWidth>
        <EmptyProfile>
          <LottieIcon
            animationData={loaderAnimation}
            trigger="loop"
            size={180}
            style={{ marginTop: "-75px" }}
          />
          <EmptyText>No luck here!</EmptyText>
          <EmptyText $light>Explore templates instead.</EmptyText>
          <Button
            onClick={() => {
              navigate("/");
              if (categories?.[0]?._id) {
                dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
              }
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          >
            Visit Explore
          </Button>
        </EmptyProfile>
      </MainContainer>
    );
  }

  return (
    <MainContainer hideTabs={true} fullWidth>
      <ProfileHeader
        style={{
          background:
            activePlan === BASIC_PLAN_NAME
              ? "radial-gradient(60.06% 100% at 50% 0%, #4C688A 0%, rgba(14, 14, 18, 0.00) 68.75%)"
              : activePlan === PRO_PLAN_NAME
                ? "radial-gradient(60.06% 100% at 50% 0%, rgba(26, 96, 185, 0.40) 0%, rgba(14, 14, 18, 0.00) 68.75%)"
                : activePlan === PREMIUM_PLAN_NAME
                  ? "radial-gradient(60.06% 100% at 50% 0%,#07305D 0%,rgba(14, 14, 18, 0) 68.75%)"
                  : "none",
        }}
      >
        {isProfileLoading ? (
          <>
            <ProfileHeaderRow>
              <ProfilePhotoContainer>
                <ProfilePhotoSkeleton $animationTime={1.5} />
              </ProfilePhotoContainer>
              <ProfileHeaderColumn>
                <TextSkeleton
                  $width="140px"
                  $height="20px"
                  $animationTime={1.5}
                />
                <TextSkeleton
                  $width="90px"
                  $height="14px"
                  $animationTime={1.5}
                />
                <StatsContainer>
                  <TextSkeleton
                    $width="60px"
                    $height="14px"
                    $animationTime={1.5}
                  />
                  <TextSkeleton
                    $width="80px"
                    $height="14px"
                    $animationTime={1.5}
                  />
                  <TextSkeleton
                    $width="80px"
                    $height="14px"
                    $animationTime={1.5}
                  />
                </StatsContainer>
                <DesktopTextSkeleton
                  $width="260px"
                  $height="14px"
                  $animationTime={1.5}
                />
              </ProfileHeaderColumn>
            </ProfileHeaderRow>
            <MobileTextSkeleton
              $width="260px"
              $height="14px"
              $animationTime={1.5}
            />
            <Buttons>
              <ButtonSkeleton $animationTime={1.5} />
            </Buttons>
          </>
        ) : (
          <>
            <ProfileHeaderRow>
              <ProfilePhotoContainer>
                {user?.profilePhoto ? (
                  <ProfilePhoto
                    src={user?.profilePhoto}
                    alt={`${user.username} profile photo`}
                    $border={
                      activePlan === BASIC_PLAN_NAME
                        ? "2px solid #486689"
                        : activePlan === PRO_PLAN_NAME
                          ? "2px solid #004FB7"
                          : activePlan === PREMIUM_PLAN_NAME
                            ? "2px solid #07305D"
                            : "none"
                    }
                  />
                ) : (
                  <DefaultProfilePhoto
                    size={isMobile ? 76 : 120}
                    style={{
                      borderRadius: "50%",
                      border:
                        activePlan === BASIC_PLAN_NAME
                          ? "2px solid #486689"
                          : activePlan === PRO_PLAN_NAME
                            ? "2px solid #004FB7"
                            : activePlan === PREMIUM_PLAN_NAME
                              ? "2px solid #07305D"
                              : "none",
                    }}
                  />
                )}
                {activePlan === BASIC_PLAN_NAME && (
                  <PlanBadge
                    $gradient="linear-gradient(338deg, #486689 1.48%, #3B4858 98.03%)"
                    $border="0.5px solid #486689"
                  >
                    Basic
                  </PlanBadge>
                )}

                {activePlan === PRO_PLAN_NAME && (
                  <PlanBadge
                    $gradient="radial-gradient(70.83% 78.5% at 54.73% 0%, #004FB7 0%, #062F6B 100%);"
                    $border="0.5px solid #004FB7"
                  >
                    Pro
                  </PlanBadge>
                )}
                {activePlan === PREMIUM_PLAN_NAME && (
                  <PlanBadge
                    $gradient="radial-gradient(70.83% 78.5% at 54.73% 0%, #07305D 0%, #010C16 100%)"
                    $border="0.5px solid #07305D"
                  >
                    Premium
                  </PlanBadge>
                )}
              </ProfilePhotoContainer>
              <ProfileHeaderColumn>
                <Username>@{user?.username}</Username>

                <StatsContainer>
                  <Stats style={{ gap: "6px" }}>
                    {user?.activeModels ?? 0}
                    <Stats $light>
                      {(user?.activeModels ?? 0) === 1
                        ? "template"
                        : "templates"}
                    </Stats>
                  </Stats>
                  <Stats style={{ gap: "6px" }}>
                    {user?.numOfFollowers ?? 0}
                    <Stats $light>
                      {(user?.numOfFollowers ?? 0) === 1
                        ? "follower"
                        : "followers"}
                    </Stats>
                  </Stats>
                  {/* <Stats
                    $click
                    onClick={() => {
                      setOpenModal("following");

                      setIsOpenFollowersModal(true);
                    }}
                  >
                    {user?.numOfFollowing ?? 0}
                    <Stats $light $click>
                      following
                    </Stats>
                  </Stats> */}
                </StatsContainer>

                <BadgesContainer>
                  {medalSrc && (
                    <MedalBadge $rank={user?.score?.creatorLeaderboardRank}>
                      <MedalIcon
                        src={medalSrc}
                        alt={`Creator leaderboard rank #${creatorRank}`}
                      />
                    </MedalBadge>
                  )}

                  {user?.isVipCreator && <VipBadge>VIP</VipBadge>}
                </BadgesContainer>
                {user?.bio && <Description>{user?.bio}</Description>}
              </ProfileHeaderColumn>
            </ProfileHeaderRow>
            {user?.bio && <MobileDescription>{user?.bio}</MobileDescription>}

            <Buttons>
              {isMyProfile ? (
                <FollowButton
                  $isFollowed
                  onClick={() => {
                    navigate("/edit-profile");
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  <EditIcon />
                  edit profile
                </FollowButton>
              ) : isFollowing ? (
                <FollowingButtonGroup ref={followMenuRef}>
                  <FollowingMainButton
                    type="button"
                    disabled={isToggleFollowPending}
                  >
                    Following
                  </FollowingMainButton>
                  <FollowingArrowButton
                    type="button"
                    disabled={isToggleFollowPending}
                    $open={showFollowMenu}
                    onClick={() => {
                      if (isToggleFollowPending) return;
                      setShowFollowMenu((prev) => !prev);
                    }}
                  >
                    <ChevronDownIcon />
                  </FollowingArrowButton>

                  {showFollowMenu && (
                    <FollowingMenu>
                      <FollowingMenuItem
                        type="button"
                        disabled={isToggleFollowPending}
                        onClick={() => {
                          if (isToggleFollowPending) return;
                          setShowFollowMenu(false);
                          dispatch(toggleFollow(user._id));
                        }}
                      >
                        <UnfollowIcon />
                        Unfollow
                      </FollowingMenuItem>
                    </FollowingMenu>
                  )}
                </FollowingButtonGroup>
              ) : (
                <FollowButton
                  $isFollowed={isFollowing}
                  disabled={isToggleFollowPending}
                  onClick={handleFollowToggle}
                >
                  <>
                    {isFollowing ? <UnfollowIcon /> : <FollowIcon />}
                    {isFollowing ? "unfollow" : "follow"}
                  </>
                </FollowButton>
              )}

              <ShareButton onClick={handleShare}>
                <ShareIcon />
                share
              </ShareButton>
            </Buttons>
          </>
        )}
      </ProfileHeader>

      <ContentContainer>
        {error ? (
          <Content>
            <EmptyState>
              <h3>We're having trouble loading videos</h3>
              <p>
                This is usually temporary. Please check your connection and try
                again.
              </p>
              <Button
                onClick={() => {
                  window.location.reload();
                }}
              >
                Retry
              </Button>
            </EmptyState>
          </Content>
        ) : loading || !data ? (
          <VideoMasonrySkeletonLoader count={24} fullWidth mobileTwoRows />
        ) : (
          <Content>
            {!data?.length ? (
              <EmptyProfile>
                <EmptyIcon
                  width={32}
                  height={32}
                  style={{ opacity: 0.4, marginBottom: 12 }}
                />
                <EmptyText $light>Nothing to see here. Yet.</EmptyText>
                <Button
                  onClick={() => {
                    navigate("/");
                    if (categories?.[0]?._id) {
                      dispatch(
                        setSelectedExploreCollectionFilter(categories[0]._id),
                      );
                    }
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });
                  }}
                >
                  Explore Templates
                </Button>
              </EmptyProfile>
            ) : (
              <LayoutGroup id="profile-pinned-collections">
                {Array.from({ length: numOfColumns }).map((_, colIndex) => {
                  return (
                    <Column key={colIndex}>
                      {data
                        .filter((_, i) => i % numOfColumns === colIndex)
                        .map((item) => {
                          const itemId = getItemId(item);

                          return (
                            <motion.div
                              key={itemId}
                              layout
                              layoutId={`profile-collection-${itemId}`}
                              style={{
                                width: "100%",
                              }}
                              transition={{
                                layout: {
                                  type: "spring",
                                  stiffness: 420,
                                  damping: 34,
                                  mass: 0.82,
                                },
                              }}
                            >
                              <ExploreItem
                                item={item}
                                hideUsername
                                isPinned={isItemPinned(item)}
                                onItemLongPress={
                                  canPinCollection(item)
                                    ? (rect) => {
                                        if (!rect) return;
                                        setPinTarget({ item, rect });
                                      }
                                    : undefined
                                }
                                onItemContextMenu={
                                  canPinCollection(item)
                                    ? (rect) => {
                                        if (!rect) return;
                                        setPinTarget((prev) => {
                                          if (prev) {
                                            return null;
                                          }

                                          return { item, rect };
                                        });
                                      }
                                    : undefined
                                }
                                onItemClick={() => {
                                  if (loggedIn) {
                                    dispatch(setSelectedCollection(item));
                                    if (
                                      item.modelType === CREATE_IMAGE_MODEL_NAME
                                    ) {
                                      setIsCreateImageModalOpen(true);
                                    } else if (item.modelType === BLEND_MODEL) {
                                      setIsGroupModalOpen(true);
                                    } else if (
                                      isImageOutput(
                                        item.modelType ?? item.modelName,
                                      )
                                    ) {
                                      setIsEditImageModalOpen(true);
                                    } else {
                                      setIsGenerateCollectionModalOpen(true);
                                    }
                                  } else {
                                    dispatch(setSignUpModalOpen(true));
                                  }
                                }}
                              />
                            </motion.div>
                          );
                        })}
                    </Column>
                  );
                })}
              </LayoutGroup>
            )}
          </Content>
        )}
      </ContentContainer>

      {total > 0 ? (
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={perPage}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      ) : null}

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
        profileUsername={username}
        profilePage
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollectionVideo}
        onClose={() => setIsEditImageModalOpen(false)}
        profileUsername={username}
        profilePage
      />

      <UserFollowersModal
        isOpen={isOpenFollowersModal}
        onClose={() => setIsOpenFollowersModal(false)}
        followers={openModal === "followers"}
        userId={user?._id}
      />

      <CreateImageModal
        isOpen={isCreateImageModalOpen}
        collection={selectedCollectionVideo}
        onClose={() => setIsCreateImageModalOpen(false)}
      />

      <GroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
      />

      {pinTarget && pinPreviewLayout && (
        <div ref={pinTooltipRef}>
          <PinPreviewBackdrop />
          <PinPreviewCard
            $top={pinPreviewLayout.preview.top}
            $left={pinPreviewLayout.preview.left}
            $width={pinPreviewLayout.preview.width}
            $height={pinPreviewLayout.preview.height}
          >
            {pinPreviewItem?.output?.type === MEDIA_TYPE.VIDEO &&
            pinPreviewMediaUrl ? (
              <PinPreviewVideo
                src={pinPreviewMediaUrl}
                poster={pinPreviewPoster || undefined}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : pinPreviewMediaUrl ? (
              <PinPreviewImage
                src={pinPreviewMediaUrl}
                alt={`${pinPreviewTitleText} preview`}
              />
            ) : null}

            <PinPreviewInfo>
              <PinPreviewTitle>{pinPreviewTitleText}</PinPreviewTitle>
              <PinPreviewSubtitle>{pinPreviewSubtitleText}</PinPreviewSubtitle>
            </PinPreviewInfo>
          </PinPreviewCard>

          <PinActionMenu
            $top={pinPreviewLayout.menu.top}
            $left={pinPreviewLayout.menu.left}
            $width={pinPreviewLayout.menu.width}
          >
            <PinActionItem
              onClick={handlePinConfirm}
              disabled={pinActionLoading}
              aria-label={
                isItemPinned(pinPreviewItem) ? "Unpin post" : "Pin post"
              }
            >
              {pinActionLoading ? (
                <l-line-spinner size="18" stroke="2" speed="1" color="#fff" />
              ) : (
                <PinIcon />
              )}
              {isItemPinned(pinPreviewItem) ? "Unpin" : "Pin"}
            </PinActionItem>
          </PinActionMenu>
        </div>
      )}
    </MainContainer>
  );
};

export default ProfilePage;
